import { memo, useCallback, useMemo } from 'react';

import { Button } from '@air/primitive-button';

import { useLibraries } from '~/components/LibraryBeta/hooks/queries/useLibraries';
import { WorkspaceSidebarCollapsible } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarCollapsible';
import { WorkspaceSidebarLibrariesHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesHeader';
import {
  WorkspaceSidebarLibrariesList,
  type WorkspaceSidebarLibrariesListProps,
} from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesList';
import { WorkspaceSidebarLibrariesListItemSkeleton } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesListItemSkeleton';
import { WorkspaceSidebarSection } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSection';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useRearrangeLibraries } from '~/components/v2/hooks/useRearrangeLibraries';
import { useWorkspaceSidebarLibrariesStore } from '~/components/v2/store/features/workspace/useWorkspaceSidebarLibrariesStore';
import { LibrarySortBy } from '~/components/v2/store/shared/creareLibrarySortSlice';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { useLibrariesBeingDeleted } from '~/swr-hooks/libraries/useLibrariesBeingDeleted';

export const WorkspaceSidebarLibraries = memo(() => {
  const librarySortBy = useWorkspaceSidebarLibrariesStore((state) => state.librarySortBy);
  const librarySortDirection = useWorkspaceSidebarLibrariesStore((state) => state.librarySortDirection);
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const {
    data: libraries,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingLibraries,
  } = useLibraries({
    options: {
      sortField: {
        name: librarySortBy,
        direction: librarySortDirection,
      },
    },
  });
  const librariesBeingDeleted = useLibrariesBeingDeleted();
  const { rearrangeLibraries } = useRearrangeLibraries();

  const flattenedLibraries = useMemo(
    () =>
      libraries?.pages.flatMap((page) => page.data).filter((library) => !librariesBeingDeleted.includes(library.id)) ??
      [],
    [libraries?.pages, librariesBeingDeleted],
  );

  useFetchObjectsPermissions({
    objects: {
      libraryIds: flattenedLibraries.map(({ id }) => id),
    },
  });

  const handleLoadNextPage = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const isDraggable = librarySortBy === LibrarySortBy.CustomSort;

  const onLibraryRearrange = useCallback<Required<WorkspaceSidebarLibrariesListProps>['onLibraryRearrange']>(
    ({ adjacentItem, libraries, library }) => {
      rearrangeLibraries({
        libraries,
        draggedLibrary: library.library,
        adjacentItem,
      });
    },
    [rearrangeLibraries],
  );

  const librariesList = isLoadingLibraries ? (
    <>
      <WorkspaceSidebarLibrariesListItemSkeleton />
      <WorkspaceSidebarLibrariesListItemSkeleton />
    </>
  ) : (
    <WorkspaceSidebarLibrariesList
      libraries={flattenedLibraries}
      draggable={isDraggable}
      onLibraryRearrange={onLibraryRearrange}
    />
  );

  return (
    <WorkspaceSidebarSection className="mt-2">
      <WorkspaceSidebarLibrariesHeader />
      <WorkspaceSidebarCollapsible open={!collapsed}>
        {librariesList}
        {hasNextPage && !isLoadingLibraries ? (
          <Button
            appearance="ghost"
            className="ml-9 justify-start"
            color="gray"
            data-testid="WORKSPACE_SIDEBAR_LIBRARIES_SHOW_MORE"
            isLoading={isFetchingNextPage}
            onClick={handleLoadNextPage}
            size="small"
          >
            Show more
          </Button>
        ) : null}
      </WorkspaceSidebarCollapsible>
    </WorkspaceSidebarSection>
  );
});

WorkspaceSidebarLibraries.displayName = 'WorkspaceSidebarLibraries';
